import Phaser from "phaser";
import { unlockAudio } from "../audio/sfx";
import { setRaidEncounterStart, setRaidParty } from "../data/raid-catalog";
import { t } from "../data/strings";
import {
  confirmRoster,
  fillDefaultRoster,
  setRosterEncounterStart,
  toggleRosterPick,
  type RaidRosterState,
} from "../features/raid";
import { SceneKey, assertTransition } from "../flow/game-flow";
import { sharedInput } from "../input/input-controller";
import { mountRaidRosterHud } from "../ui/huds/raid/roster";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

/**
 * Raid Leader — party roster select → battle.
 * HUD shows per-hero disposition / engagement / gambits (catalog-driven).
 */
export class RaidRosterScene extends Phaser.Scene {
  private disposed = false;
  private state!: RaidRosterState;
  private hud: ReturnType<typeof mountRaidRosterHud> | null = null;

  constructor() {
    super(SceneKey.RaidRoster);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.RaidRoster);
    sharedInput.attach();
    clearOverlay();
    this.state = fillDefaultRoster();

    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor("#0c1424");
    this.add
      .rectangle(width / 2, height / 2, width, height, 0x0c1424)
      .setDepth(-1);
    this.add
      .text(width / 2, height * 0.08, t("raid.roster.title"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "28px",
        color: "#ffd27a",
      })
      .setOrigin(0.5);
    this.add
      .text(width / 2, height * 0.125, t("raid.roster.subtitle"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "13px",
        color: "#9aa3c7",
      })
      .setOrigin(0.5);

    this.hud = mountRaidRosterHud({
      onToggle: (defId) => {
        unlockAudio();
        this.state = toggleRosterPick(this.state, defId);
        this.hud?.sync(this.state);
      },
      onEncounterStart: (mode) => {
        unlockAudio();
        this.state = setRosterEncounterStart(this.state, mode);
        this.hud?.sync(this.state);
      },
      onConfirm: () => {
        unlockAudio();
        const r = confirmRoster(this.state);
        if (!r.ok) {
          this.state = { ...this.state, messageKey: r.messageKey };
          this.hud?.sync(this.state);
          return;
        }
        setRaidParty(r.party);
        setRaidEncounterStart(this.state.encounterStart);
        assertTransition(SceneKey.RaidRoster, SceneKey.RaidBattle);
        this.scene.start(SceneKey.RaidBattle);
      },
      onBack: () => this.backToHub(),
    });
    this.hud.sync(this.state);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) this.backToHub();
  }

  private backToHub(): void {
    assertTransition(SceneKey.RaidRoster, SceneKey.RaidHub);
    this.scene.start(SceneKey.RaidHub);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.hud?.destroy();
    this.hud = null;
  }
}
